import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
  Box,
  Typography,
  Divider,
  Alert,
} from '@mui/material';
import {
  Public as PublicIcon,
  Group as TeamIcon,
  Lock as PrivateIcon,
} from '@mui/icons-material';
import { ProfilePrivacy } from '../../types';

interface ProfilePrivacyDialogProps {
  open: boolean;
  onClose: () => void;
  currentPrivacy: ProfilePrivacy;
  onSave: (privacy: ProfilePrivacy) => Promise<void>;
}

const ProfilePrivacyDialog: React.FC<ProfilePrivacyDialogProps> = ({
  open,
  onClose,
  currentPrivacy,
  onSave,
}) => {
  const [privacy, setPrivacy] = useState<ProfilePrivacy>(currentPrivacy);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setPrivacy(currentPrivacy);
  }, [currentPrivacy, open]);

  const handleVisibilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrivacy({
      ...privacy,
      visibility: event.target.value as 'public' | 'team-only' | 'private',
    });
  };

  const handleToggleChange = (field: keyof Omit<ProfilePrivacy, 'visibility'>) => 
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setPrivacy({
        ...privacy,
        [field]: event.target.checked,
      });
    };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await onSave(privacy);
      onClose();
    } catch (err: any) {
      console.error('Failed to save privacy settings:', err);
      setError(err.message || 'Failed to save privacy settings');
    } finally {
      setSaving(false);
    }
  };

  const getVisibilityDescription = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return 'Anyone with the link can view your profile, even if they don\'t have an account.';
      case 'team-only':
        return 'Only members of your teams can view your profile.';
      case 'private':
        return 'Only you can view your profile. No one else can see it.';
      default:
        return '';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Profile Privacy Settings</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Control who can see your profile and what information is visible
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Visibility Level */}
        <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
          <FormLabel component="legend" sx={{ mb: 2 }}>
            Who can view your profile?
          </FormLabel>
          <RadioGroup
            value={privacy.visibility}
            onChange={handleVisibilityChange}
          >
            <Box sx={{ mb: 2 }}>
              <FormControlLabel 
                value="public" 
                control={<Radio />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PublicIcon fontSize="small" />
                    <Box>
                      <Typography variant="body1">Public</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getVisibilityDescription('public')}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel 
                value="team-only" 
                control={<Radio />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TeamIcon fontSize="small" />
                    <Box>
                      <Typography variant="body1">Team Only</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getVisibilityDescription('team-only')}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Box>

            <Box>
              <FormControlLabel 
                value="private" 
                control={<Radio />} 
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PrivateIcon fontSize="small" />
                    <Box>
                      <Typography variant="body1">Private</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {getVisibilityDescription('private')}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </Box>
          </RadioGroup>
        </FormControl>

        <Divider sx={{ my: 3 }} />

        {/* What to Show */}
        <FormControl component="fieldset" fullWidth>
          <FormLabel component="legend" sx={{ mb: 2 }}>
            What information to display
          </FormLabel>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={privacy.showAvatar}
                  onChange={handleToggleChange('showAvatar')}
                  disabled={privacy.visibility === 'private'}
                />
              }
              label="Show Avatar"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={privacy.showBadges}
                  onChange={handleToggleChange('showBadges')}
                  disabled={privacy.visibility === 'private'}
                />
              }
              label="Show Badges & Achievements"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={privacy.showPRs}
                  onChange={handleToggleChange('showPRs')}
                  disabled={privacy.visibility === 'private'}
                />
              }
              label="Show Personal Records"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={privacy.showStats}
                  onChange={handleToggleChange('showStats')}
                  disabled={privacy.visibility === 'private'}
                />
              }
              label="Show Stats & Points"
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={privacy.showTeam}
                  onChange={handleToggleChange('showTeam')}
                  disabled={privacy.visibility === 'private'}
                />
              }
              label="Show Team Information"
            />
          </Box>

          {privacy.visibility === 'private' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Privacy settings are disabled because your profile is set to Private
            </Alert>
          )}
        </FormControl>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfilePrivacyDialog;
